import React, { useState, useEffect, useRef } from 'react';
import sanitizeHtml from 'sanitize-html';
import {
  Box, Typography, Container, TextField, Stack, Alert, Button,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  Checkbox, FormGroup, Paper, Divider, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import API_ROUTES from '../config/api';
import { apiFetch } from '../apiFetch';
import { logger } from '../services/logger';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../i18n/i18n';

interface SessionLevel {
  LEVEL_NAME: string;
}

interface UserPreference {
  GUID: string;
  CHANNEL: string;
  CHANNEL_USER_IDENTITY: string;
  DAYS_AHEAD: number; // Unused
  FOR_LEVELS: string[];
  SIDE_PREFERENCE: string[];
  PREFERRED_DAY_OF_WEEK?: number[];
  PREFERRED_TIME_OF_DAY?: string[];
  AVAILABLE_MIN: number;
  EXPIRY_PERIOD?: number;
  // FREQUENCY?: string;
  START_TIME?: number;
  IS_ACTIVE?: boolean;
  CREATED_AT?: string;
  UPDATED_AT?: string;
  FCM_TOKEN?: string; // New field for push notifications
}

interface Session {
  ID: number;
  SESSION_DATE: string;
  SESSION_TIME: string;
  SESSION_DATETIME_UTCISO: string; // ISO format date-time string
  SESSION_TITLE: string;
  SESSION_SIDE: string;
  AVAILABLE_SPOTS: number;
  LEVEL_NAME: string;
  STATUS?: string;
  EXT_URL?: string;
}

// Determine direction based on language
const direction = i18n.language === 'he' ? 'rtl' : 'ltr';

// Styled MuiLinkButton to match radio button font size and color
const MuiLinkButton = styled('a')(({ theme }) => ({
  color: 'black',
  fontSize: theme.typography.body1.fontSize,
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
  padding: 0,
  lineHeight: 1.2,
  marginBottom: '4px',
  minWidth: 'unset',
  display: 'inline-block',
  transition: 'color 0.2s',
  '&:hover, &:focus': {
    color: 'black',
  },
}));

const StepNumber: React.FC<{ step: number }> = ({ step }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      color: '#86adc1',
      fontFamily: '"Grobold", "Arial Rounded MT Bold", "Arial Rounded Bold", "Trebuchet MS", "Segoe UI", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: 0,
      verticalAlign: 'baseline',
      textShadow: '0 1px 0 rgba(255, 255, 255, 0.45), 0 1.5px 2px rgba(0, 0, 0, 0.12)',
      minWidth: '1.1rem',
      textAlign: 'right',
      mr: 0.75,
      flexShrink: 0,
    }}
  >
    {step}.
  </Box>
);

const renderStepLabel = (step: number, label: string) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', verticalAlign: 'middle', gap: '6px' }}>
    <StepNumber step={step} />
    <Box component="span">{label}</Box>
  </Box>
);

const theme = createTheme({
  direction: direction,
  typography: {
    fontFamily: 'Arial, sans-serif',
    fontSize: 14,
    body1: {
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      lineHeight: 1.2,
      marginBottom: '4px',
      color: '#0077b6', // Wave blue for headings
      // whiteSpace: 'nowrap',
    },
  },
  palette: {
    primary: {
      main: '#0077b6', // Ocean blue
    },
    secondary: {
      main: '#00b4d8', // Wave blue
    },
    success: {
      main: '#2e7d32', // Green for success
    },
    error: {
      main: '#f94144', // Red for errors
    },
    background: {
      default: '#caf0f8', // Light blue background
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.4rem', // Make dialog titles bigger
          fontWeight: 600,
          paddingBottom: '16px', // More space below title
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px', // More padding around action buttons
          '& .MuiButton-root': {
            fontSize: '1.1rem', // Make dialog action buttons bigger
            padding: '10px 24px', // More padding for action buttons
            minWidth: '100px', // Ensure minimum width for action buttons
            marginLeft: '8px', // Space between action buttons
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '4px', // Default padding for radio buttons
          '&.Mui-checked': {
            color: '#1976d2', // Blue color when checked
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '4px', // Default padding for checkboxes
          '&.Mui-checked': {
            color: '#1976d2', // Blue color when checked
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // Prevent uppercase transformation
          fontSize: '14px',
          lineHeight: 1.1, // Make wrapped text lines closer together
          minWidth: 'unset',
          // For dialogs, these will be overridden by DialogContent and DialogActions styles
        },
        contained: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)', // Add subtle shadow to contained buttons
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)', // Enhance shadow on hover
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          minHeight: '32px',
          paddingTop: 0,
          paddingBottom: 0,
          '& input': {
            paddingTop: '3px',
            paddingBottom: '3px',
            fontSize: '1rem',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
          marginTop: '2px',
        },
      },
    },
  },
});

const VERIFICATION_CODE_KEY = 'srf_verification_code';
const EMAIL_KEY = 'srf_email';

const SessionFinderForm: React.FC = () => {
  // Expand/Collapse visibility state of filters
  const [levelsVisible, setLevelsVisible] = useState(true);
  const [sideVisible, setSideVisible] = useState(false);
  const [daysVisible, setDaysVisible] = useState(false);
  const [timeVisible, setTimeVisible] = useState(false);
  const [spotsVisible, setSpotsVisible] = useState(true);
  const [startTimeVisible, setStartTimeVisible] = useState(false);
  // Ref for verification code input
  const verificationCodeRef = useRef<HTMLInputElement>(null);
  // Ref for email input so we can focus/scroll to it from the UI
  const emailInputRef = useRef<HTMLInputElement>(null);
  const { t: tOrig } = useTranslation();
  // Fallback: return "***key***" if key is missing
  const t = (key: string, opts?: any) => {
    const val = tOrig(key, opts);
    if (val === key) return `***${key}***`;
    return val;
  };

  const [sessionLevels, setSessionLevels] = useState<SessionLevel[]>([]);
  const [sessionLevelsLoaded, setSessionLevelsLoaded] = useState(false);
  const [previewSessions, setPreviewSessions] = useState<Session[]>([]);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionDialogMessage, setActionDialogMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const FCM_TOKEN_HASH_KEY = 'srf_fcm_token_hash';
  const [formData, setFormData] = useState({
    email: localStorage.getItem(EMAIL_KEY) || '',
    verificationCode: localStorage.getItem(VERIFICATION_CODE_KEY) || '', // <-- read from localStorage here
    isVerified: false,
    selectedLevels: [] as string[],
    sidePreference: 'any' as 'any' | 'LEFT' | 'RIGHT',
    preferredDayOfWeek: [0] as number[], // array of integers represending days like: [] or [0] or [2, 4, 5]
    preferredTimeOfDay: [] as string[], // array of strings representing times like: [] or ['08:00', '10:00']
    startTime: 24, // start searching 24 hours before the session starts
    // frequency: 'once' as 'once' | 'onChange',
    expiryPeriod: 4, // 4 weeks
    minSpots: 0, // any number of spots, 0 means no limit
    preferenceGUID: '',
    isActive: true,
    pushToken: '',
  });

  // Utility: hash string with SHA-256 and return hex
  const hashString = async (str: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  };
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(t('loading.default')); // Add loading message state
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [tempSelectedTimes, setTempSelectedTimes] = useState<string[]>([]);
  const [showFcmErrorDialog, setShowFcmErrorDialog] = useState(false);
  const [fcmErrorDetails, setFcmErrorDetails] = useState<string | null>(null);
  const [showFcmInitDialog, setShowFcmInitDialog] = useState(false); // NEW: show FCM init dialog
  const [showBelowCode, setShowBelowCode] = useState(!!localStorage.getItem(VERIFICATION_CODE_KEY));
  const sendCodeTriggeredAlert = useRef(false);
  // Add retry utility function for serverless cold starts (client-side only)
  const fetchWithRetryClientSide = async (url: string, options: RequestInit = {}, maxRetries = 3) => {
    let lastWasNonDefault = false;
    try {
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          // Update loading message for attempts
          if (attempt === 0) {
            setLoadingMessage(t('loading.connecting'));
            lastWasNonDefault = true;
          } else if (attempt === 1) {
            setLoadingMessage(t('loading.waking'));
            lastWasNonDefault = true;
          } else {
            setLoadingMessage(t('loading.retrying'));
            lastWasNonDefault = true;
          }

          // Extended timeout for serverless cold start (45 seconds)
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 45000);
          const response = await apiFetch(url, {
            ...options,
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          // If successful, return the response
          if (response.ok) {
            return response;
          }
          
          // If it's a server error (5xx) or timeout-related, retry
          if (response.status >= 500 || response.status === 408 || response.status === 429) {
            throw new Error(`Server error: ${response.status}`);
          }
          
          // For other errors (4xx), don't retry
          return response;
          
        } catch (error) {
          // If it's the last attempt, throw the error
          if (attempt === maxRetries) {
            throw error;
          }
          // Calculate delay: 2s, 4s, 6s
          const delay = 2000 * (attempt + 1);
          setLoadingMessage(t('loading.retryIn', { seconds: delay / 1000 }));
          lastWasNonDefault = true;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      throw new Error('Max retries exceeded');
    } finally {
      if (lastWasNonDefault) setLoadingMessage(t('loading.default'));
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const preferenceGUID = urlParams.get('pid');
        const action = urlParams.get('action');
        const code = urlParams.get('code');

        // Persist verification code from URL into localStorage and form state
        if (code) {
          localStorage.setItem(VERIFICATION_CODE_KEY, code);
          setFormData(prev => ({ ...prev, verificationCode: code }));
          setShowBelowCode(true);
        }

        // Handle URL actions first
        if (preferenceGUID && action) {
          await handleUrlAction(preferenceGUID, action, code ?? undefined);
        }

        await loadSessionLevels();

        const overridesParam = urlParams.get('overrides');

        // Load preference by GUID if `pid` exists
        if (preferenceGUID) {
          await fetchPreferenceByGUID(preferenceGUID);
        }

        // Apply overrides after all loading is complete
        if (overridesParam) {
          const updatedFormData = parseAndMergeOverrides(overridesParam);
          if (updatedFormData) {
            setFormData(updatedFormData);
            console.log('Form updated with overrides:', updatedFormData);
          }
        }
      } catch (error) {
        console.error('Error during initialization:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleUrlAction = async (guid: string, action: string, code?: string) => {
    try {
      let endpoint = '';

      switch (action) {
        case 'activate':
          if (!code) { console.warn('activate action missing code param'); return; }
          endpoint = `${API_ROUTES.preferenceAction}?pid=${guid}&action=activate&code=${encodeURIComponent(code)}`;
          break;
        case 'continue_now':
          endpoint = `${API_ROUTES.preferenceAction}?pid=${guid}&action=continue`;
          break;
        case 'continue_tomorrow':
          endpoint = `${API_ROUTES.preferenceAction}?pid=${guid}&action=continue&in=24`;
          break;
        case 'stop':
          endpoint = `${API_ROUTES.preferenceAction}?pid=${guid}&action=stop_now`;
          break;
        default:
          console.warn('Unknown action:', action);
          return;
      }

      const response = await fetchWithRetryClientSide(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }, 3); // 3 retries for page load actions

      const result = await response.json();

      if (response.ok) {
        setActionDialogMessage({ type: 'success', text: result.message || t('dialogs.action.success') });
      } else {
        setActionDialogMessage({ type: 'error', text: result.detail || t('dialogs.action.error') });
      }
      setShowActionDialog(true);
    } catch (error) {
      console.error('Error handling URL action:', error);
      setActionDialogMessage({ type: 'error', text: t('dialogs.action.serverError') });
      setShowActionDialog(true);
    }
  };

  const loadSessionLevels = async () => {
    try {
      const response = await fetchWithRetryClientSide(API_ROUTES.sessionLevels, {}, 3); // 3 retries for page load
      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`[loadSessionLevels] Failed response: ${errorText}`);
        throw new Error(`Failed to load session levels: ${response.statusText}`);
      }
      const levels = await response.json();
      logger.info(`[loadSessionLevels] Successfully loaded levels: ${JSON.stringify(levels)}`);
      setSessionLevels(levels);
      setLoadingMessage(t('loading.default'));
    } catch (error) {
      logger.error('Failed to load session levels after retries:', error);
      setMessage({ type: 'error', text: t('dialogs.loadSessionLevels.error') });
      setShowAlert(true);
    } finally {
      setSessionLevelsLoaded(true);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (email: string) => {
    setFormData({ ...formData, email });
    
    if (email && !validateEmail(email)) {
      setEmailError(t('form.invalidEmail'));
    } else {
      setEmailError('');
    }
  };
  

  const sendVerificationCode = async () => {
    if (formData.selectedLevels.length === 0) {
      setMessage({ type: 'error', text: t('dialogs.preview.selectSession') });
      setShowAlert(true);
      setLevelsVisible(true);
      return;
    }

    if (!formData.email) {
      setMessage({ type: 'error', text: t('form.enterEmail') });
      setShowAlert(true);
      return;
    }

    if (!validateEmail(formData.email)) {
      setMessage({ type: 'error', text: t('form.invalidEmail') });
      setShowAlert(true);
      return;
    }

    setLoading(true);
    setLoadingMessage(t('loading.default'));
    try {
      const body = {
        CHANNEL: 'EMAIL',
        CHANNEL_USER_IDENTITY: formData.email,
        VERIFICATION_CODE: localStorage.getItem(VERIFICATION_CODE_KEY) || formData.verificationCode,
        DAYS_AHEAD: -1,
        FOR_LEVELS: formData.selectedLevels,
        SIDE_PREFERENCE: formData.sidePreference === 'any' ? [] : [formData.sidePreference],
        PREFERRED_DAY_OF_WEEK: formData.preferredDayOfWeek,
        AVAILABLE_MIN: formData.minSpots,
        EXPIRY_PERIOD: formData.expiryPeriod,
        START_TIME: formData.startTime,
        PREFERRED_TIME_OF_DAY: formData.preferredTimeOfDay,
        GUID: formData.preferenceGUID || undefined,
        FCM_TOKEN: formData.pushToken || undefined,
      };

      const response = await fetchWithRetryClientSide(`${API_ROUTES.preferences}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, 3);

      const result = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: result.message });
      } else {
        const { error_key, message } = result.detail;
        setMessage({ type: error_key === 'verification.link_sent' ? 'success' : 'error', text: message });
      }
      sendCodeTriggeredAlert.current = true;
      setShowAlert(true);
    } catch (error) {
      logger.error('Failed to send verification code', error);
      setMessage({ type: 'error', text: t('form.serverError') });
      sendCodeTriggeredAlert.current = true;
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertClose = () => {
    setShowAlert(false);
    if (sendCodeTriggeredAlert.current) {
      sendCodeTriggeredAlert.current = false;
      setShowBelowCode(true);
    }
  };

  const submitWithValues = async (values: typeof formData) => {
    setLoading(true);
    try {
      const sidePrefs = values.sidePreference === 'any' ? [] : [values.sidePreference];

      const requestData = {
        CHANNEL: 'EMAIL',
        CHANNEL_USER_IDENTITY: values.email,
        VERIFICATION_CODE: values.verificationCode,
        DAYS_AHEAD: -1,
        FOR_LEVELS: values.selectedLevels,
        SIDE_PREFERENCE: sidePrefs,
        PREFERRED_DAY_OF_WEEK: values.preferredDayOfWeek,
        AVAILABLE_MIN: values.minSpots,
        EXPIRY_PERIOD: values.expiryPeriod,
        START_TIME: values.startTime,
        PREFERRED_TIME_OF_DAY: values.preferredTimeOfDay,
        GUID: values.preferenceGUID,
        FCM_TOKEN: values.pushToken,
      };

      console.log('Sending form data to backend:', requestData);

      const response = await fetchWithRetryClientSide(`${API_ROUTES.preferences}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      }, 3);

      const result = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: t('dialogs.submit.success') });
        setShowAlert(true);
        setEmailError('');

        const pid = result.pid;
        setFormData({ ...values, preferenceGUID: pid });

        const url = new URL(window.location.href);
        url.searchParams.set('pid', pid);
        window.history.replaceState({}, '', url.toString());

        await fetchPreferenceByGUID(pid);
      } else {
        const { error_key, message } = result.detail;
        setMessage({ type: error_key === 'verification.link_sent' ? 'success' : 'error', text: message });
        setShowAlert(true);
      }
    } catch (error) {
      logger.error('Failed to save preferences', error);
      setMessage({ type: 'error', text: t('form.serverError') });
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.selectedLevels.length === 0) {
      setMessage({ type: 'error', text: t('dialogs.preview.selectSession') });
      setShowAlert(true);
      setLevelsVisible(true);
      return;
    }
    await submitWithValues(formData);
  };

  const fetchPreferenceByGUID = async (guid: string) => {
    try {
      const response = await fetchWithRetryClientSide(`${API_ROUTES.preferences}/get/${guid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }, 2); // 2 retries for page load
      
      if (response.ok) {
        const preference = await response.json();
        let pushToken = '';
        if (preference.FCM_TOKEN) {
          // Check if the FCM token matches the stored hash
          // If it does, use it; if not, clear the token and remove the hash
          const localHash = localStorage.getItem(FCM_TOKEN_HASH_KEY);
          const prefHash = await hashString(preference.FCM_TOKEN);
          if (localHash && prefHash === localHash) {
            pushToken = preference.FCM_TOKEN;
          } else {
            pushToken = '';
            localStorage.removeItem(FCM_TOKEN_HASH_KEY);
          }
        }
        setFormData(prev => ({
          ...prev,
          preferenceGUID: preference.GUID,
          email: preference.CHANNEL_USER_IDENTITY,
          selectedLevels: preference.FOR_LEVELS || [],
          sidePreference: preference.SIDE_PREFERENCE?.[0] || 'any',
          preferredDayOfWeek: preference.PREFERRED_DAY_OF_WEEK || [0],
          preferredTimeOfDay: preference.PREFERRED_TIME_OF_DAY || [],
          startTime: preference.START_TIME || 24,
          minSpots: preference.AVAILABLE_MIN || 0,
          expiryPeriod: preference.EXPIRY_PERIOD || 4,
          isActive: preference.IS_ACTIVE,
          pushToken: pushToken,
          verificationCode: localStorage.getItem(VERIFICATION_CODE_KEY) || prev.verificationCode,
        }));
      } else {
        console.warn('Failed to load preference by GUID:', guid);
        setMessage({ type: 'error', text: t('dialogs.loadPreference.error') });
        setShowAlert(true);
      }
    } catch (e) {
      console.error('Error loading preference by GUID:', e);
      setMessage({ type: 'error', text: t('dialogs.loadPreference.error') });
      setShowAlert(true);
    }
  };

  const deletePreference = async (preferenceId: string) => {
    try {
      const response = await apiFetch(`${API_ROUTES.preferences}/delete/${preferenceId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('dialogs.delete.success') });
        setShowAlert(true);
      }
    } catch (error) {
      logger.error('Failed to delete preference', error);
      setMessage({ type: 'error', text: t('dialogs.delete.error') });
      setShowAlert(true);
    }
  };

  // Time dialog functions
  const working_hours_from = 6; // time first session begins
  const working_hours_until = 22; // time last session begins

  const openTimeDialog = () => {
    setTempSelectedTimes([...formData.preferredTimeOfDay]);
    setShowTimeDialog(true);
  };

  const handleTimeConfirm = () => {
    setFormData({ ...formData, preferredTimeOfDay: [...tempSelectedTimes] });
    setShowTimeDialog(false);
  };

  const handleTimeCancel = () => {
    setTempSelectedTimes([]);
    setShowTimeDialog(false);
  };

  const getTimeDisplayText = () => {
    if (formData.preferredTimeOfDay.length === 0) {
      return t('form.anyTime');
    }
    
    const sortedTimes = [...formData.preferredTimeOfDay].sort();
    return sortedTimes.join(', ');
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = working_hours_from; hour <= working_hours_until; hour++) {
      const timeString = `${hour.toString().padStart(2, '0')}:00`;
      options.push(timeString);
    }
    return options;
  };

  const getSummary = () => {
    // if (formData.selectedLevels.length === 0) {
    //   return (
    //     <Typography variant="body1" color="text.secondary">
    //       {t('summary.placeholder')}
    //     </Typography>
    //   );
    // }

    const levelsText = formData.selectedLevels.length === 0 
      ? t('summary.allLevels')
      : formData.selectedLevels.join(' ' + t('summary.or') + ' ');

    const sideText = formData.sidePreference === 'any'
      ? t('summary.sideAny')
      : formData.sidePreference === 'LEFT'
        ? t('summary.sideLeft')
        : t('summary.sideRight');

    const startTimeText = formData.startTime === 337
      ? t('summary.twoWeeks')
      : formData.startTime === 168
        ? t('form.noticeWeek')
        : formData.startTime === 48
          ? t('form.notice48h')
          : formData.startTime === 24
            ? t('form.notice24h')
            : formData.startTime === 12
              ? t('form.notice12h')
              : formData.startTime === 2
              ? t('form.notice2h')
              : '--';

    const minSpotsText = formData.minSpots === 0
      ? t('summary.anySpots')
      : `${formData.minSpots} ${t('summary.orMore')}`;
    
    const dayNames = [
      '',
      t('days.sunday'),
      t('days.monday'),
      t('days.tuesday'),
      t('days.wednesday'),
      t('days.thursday'),
      t('days.friday'),
      t('days.saturday')
    ];
    const daysText = formData.preferredDayOfWeek.includes(0) 
      ? t('summary.anyDay')
      : t('summary.days', { days: formData.preferredDayOfWeek.map(d => dayNames[d]).join(' ' + t('summary.or') + ' ') });
    
    const timeText = formData.preferredTimeOfDay.length === 0 
      ? t('summary.anyTime')
      : t('summary.times', { times: formData.preferredTimeOfDay.sort().join(', ') });
    
    // const emailText = formData.email || '[כתובת אימייל]';
    
    return (
      <Typography variant="body1">
        {t('summary.prefix')}{' '}
        <strong>{levelsText}</strong>,{' '}
        <strong>{sideText}</strong>,{' '}
        <strong>{daysText}</strong>,{' '}
        <strong>{timeText}</strong>,{' '}
        {t('summary.withMinSpots')} <strong>{minSpotsText}</strong> {t('summary.spots')},{' '}
        {t('summary.startingIn')} <strong>{startTimeText}</strong> {t('summary.startingInSuffix')}
        {/* למשך <strong>{expiryText}</strong>, */}
        {/* לאימייל <strong>{emailText}</strong> */}
      </Typography>
    );
  };

  const handlePreviewSessions = async (considerStartTime: boolean) => {
    if (considerStartTime && formData.selectedLevels.length === 0) {
      setMessage({ type: 'error', text: t('dialogs.preview.selectSession') });
      setShowAlert(true);
      setLevelsVisible(true);
      return;
    }

    setLoading(true); // Set loading to true
    try {
      // collapse all filters
      setLevelsVisible(false);
      setSideVisible(false);
      setDaysVisible(false);
      setTimeVisible(false);
      setSpotsVisible(false);
      setStartTimeVisible(false);

      // Convert form data to API format (same as submit)
      const sidePrefs = formData.sidePreference === 'any' ? [] : [formData.sidePreference];
      // const daysAhead = formData.startTime === 24 ? 1 : 2;

      const requestData = {
        CHANNEL: 'EMAIL',
        CHANNEL_USER_IDENTITY: formData.email || 'preview@example.com',
        VERIFICATION_CODE: 'preview', // Not needed for filtering
        DAYS_AHEAD: -1, // daysAhead,
        FOR_LEVELS: formData.selectedLevels,
        SIDE_PREFERENCE: sidePrefs,
        PREFERRED_DAY_OF_WEEK: formData.preferredDayOfWeek,
        AVAILABLE_MIN: formData.minSpots,
        EXPIRY_PERIOD: formData.expiryPeriod,
        START_TIME: considerStartTime ? formData.startTime : 168,
        PREFERRED_TIME_OF_DAY: formData.preferredTimeOfDay,
      };

      console.log('Preview request data:', requestData);

      const response = await apiFetch(API_ROUTES.sessionsFilter, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      const sessions = await response.json();
      
      // log response
      console.log('Preview sessions response:', sessions);

      if (response.ok) {
        setPreviewSessions(sessions);
        setShowPreviewDialog(true);
      } else {
        setMessage({ type: 'error', text: sessions.detail || t('dialogs.preview.error') });
        setShowAlert(true);
      }
    } catch (error) {
      logger.error('Failed to preview sessions', error);
      setMessage({ type: 'error', text: t('error.general') });
      setShowAlert(true);
    } finally {
      setLoading(false); // Set loading to false
    }
  };

  const parseAndMergeOverrides = (overrides: string) => {
    try {
      const parsed = JSON.parse(overrides);
      const updatedFormData = { ...formData };

      if (parsed.email) {
        updatedFormData.email = parsed.email;
      }
      if (parsed.vcode) {
        updatedFormData.verificationCode = parsed.vcode;
      }
      if (parsed.levels) {
        updatedFormData.selectedLevels = parsed.levels;
      }
      if (parsed.side) {
        updatedFormData.sidePreference = parsed.side;
      }
      if (parsed.days) {
        updatedFormData.preferredDayOfWeek = parsed.days;
      }
      if (parsed.time) {
        updatedFormData.preferredTimeOfDay = parsed.time;
      }
      if (parsed.notice) {
        updatedFormData.startTime = parsed.notice;
      }
      if (parsed.spots) {
        updatedFormData.minSpots = parsed.spots;
      }
      if (parsed.expiry) {
        updatedFormData.expiryPeriod = parsed.expiry;
      }

      return updatedFormData;
    } catch (e) {
      console.warn('Invalid overrides parameter:', overrides);
      return null;
    }
  };

  useEffect(() => {
    if (sessionLevels.length === 0) return;
    setFormData(prev => {
      if (!prev.selectedLevels || prev.selectedLevels.length === 0) return prev;
      const validLevels = sessionLevels.map(l => l.LEVEL_NAME);
      const filtered = prev.selectedLevels.filter(l => validLevels.includes(l));
      if (filtered.length !== prev.selectedLevels.length) {
        const removed = prev.selectedLevels.filter(l => !validLevels.includes(l));
        if (removed.length > 0) {
          console.warn('Removed invalid selectedLevels:', removed);
        }
        return { ...prev, selectedLevels: filtered };
      }
      return prev;
    });
  }, [sessionLevels, formData.selectedLevels]);

  // Save verification code to localStorage whenever it changes
  useEffect(() => {
    if (formData.verificationCode) {
      localStorage.setItem(VERIFICATION_CODE_KEY, formData.verificationCode);
    }
  }, [formData.verificationCode]);

  // Save email to localStorage whenever it changes
  useEffect(() => {
    if (formData.email) {
      localStorage.setItem(EMAIL_KEY, formData.email);
    }
  }, [formData.email]);

  // --- iOS Safari PWA Detection ---
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator as any).standalone;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOS_Safari_NotPWA = isIOS && isSafari && !isInStandaloneMode;
  useEffect(() => {
    console.log('[Push UX] isIOS:', isIOS, '| isSafari:', isSafari, '| isInStandaloneMode:', isInStandaloneMode, '| isIOS_Safari_NotPWA:', isIOS_Safari_NotPWA);
  }, []);

  // Modal for iPhone Home Screen instructions
  const [showIphoneHomeScreenDialog, setShowIphoneHomeScreenDialog] = useState(false);

  // Add FCM registration logic
  const handlePushCheckboxChange = async (checked: boolean) => {
    setFormData(prev => ({ ...prev, pushToken: '' }));
    if (checked) {
      setShowFcmInitDialog(true);
      try {
        logger.info("FCM: Requesting token (eager mode)...");
        if (!window.getFcmToken) {
          const errMsg = t('push.fcmNotDefined');
          logger.error("FCM: " + errMsg);
          setFcmErrorDetails(errMsg);
          setShowFcmErrorDialog(true);
          setShowFcmInitDialog(false);
          return;
        }
        const token = await window.getFcmToken();
        logger.info("FCM: Token result:", token);
        if (!token) {
          const errMsg = t('push.fcmDenied');
          logger.error("FCM: " + errMsg);
          setFcmErrorDetails(errMsg);
          setShowFcmErrorDialog(true);
          setShowFcmInitDialog(false);
          return;
        }
        // Store FCM_TOKEN hash in localStorage
        const hash = await hashString(token);
        localStorage.setItem(FCM_TOKEN_HASH_KEY, hash);
        setFormData(prev => ({ ...prev, pushToken: token }));
      } catch (e: any) {
        logger.error("FCM: Exception during registration", e);
        setFcmErrorDetails(e?.message || String(e));
        setShowFcmErrorDialog(true);
        setFormData(prev => ({ ...prev, pushToken: '' }));
      } finally {
        setShowFcmInitDialog(false);
      }
    } else {
      setFormData(prev => ({ ...prev, pushToken: '' }));
    }
  };

  const handleTestPushClick = async () => {
    setLoading(true);
    setShowAlert(false);
    let didSetNonDefault = false;
    // Countdown: 5..4..3..2..1..
    for (let secondsLeft = 5; secondsLeft > 0; secondsLeft--) {
      setLoadingMessage(t('dialogs.push.sendingTest', { secondsLeft }));
      didSetNonDefault = true;
      // eslint-disable-next-line no-await-in-loop
      if (secondsLeft > 1) {
        // Wait 1 second between countdown steps
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    try {
      const res = await apiFetch(
        `${API_ROUTES.sendPushNotification}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fcm_token: formData.pushToken,
            channel: 'EMAIL',
            channel_user_identity: formData.email,
            device_info: navigator.userAgent,
          }),
        }
      );
      const result = await res.json();
      if (res.ok && result.success) {
        setMessage({ type: 'success', text: t('dialogs.push.testSent') });
      } else {
        setMessage({ type: 'error', text: result.message || t('dialogs.push.testError') });
        setFormData(prev => ({ ...prev, pushToken: '' }));
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('dialogs.push.testError') });
      setFormData(prev => ({ ...prev, pushToken: '' }));
    } finally {
      if (didSetNonDefault) setLoadingMessage(t('loading.default'));
      setLoading(false);
      setShowAlert(true);
    }
  };

  // Extracted submit button disabled logic
  const submitDisabled =
    loading ||
    // formData.selectedLevels.length === 0 ||
    !formData.email ||
    !!emailError ||
    !formData.verificationCode;

  // Add missing required field checks for visual cues
  const missingEmail = !formData.email && submitDisabled;
  const missingCode = !formData.verificationCode && submitDisabled;

  return (
    <ThemeProvider theme={theme}>
      <Container
        maxWidth="md"
        dir={direction}
        sx={{
          py: 3,
          backgroundColor: '#caf0f8', // Light blue background
          borderRadius: '16px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          position: 'relative', // Ensure the overlay is positioned correctly
        }}
      >
        {/* ...existing code... */}
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'fixed', // Changed from 'absolute' to 'fixed'
              top: 0,
              left: 0,
              width: '100vw', // Full viewport width
              height: '100vh', // Full viewport height
              backgroundColor: 'rgba(255, 255, 255, 0.8)', // Slightly more opaque
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999, // Higher z-index to ensure it's on top
            }}
            dir={direction}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                backgroundColor: 'white',
                padding: 3,
                borderRadius: 2,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ color: 'text.secondary', whiteSpace: 'pre-line', textAlign: 'center' }}>
                {loadingMessage}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Header Section */}
        <Box
          sx={{
            position: 'relative', overflow: 'hidden',
            background: 'linear-gradient(175deg, #002d6e 0%, #004ea3 50%, #0077b6 100%)',
            color: '#fff', pt: 3, pb: '30px', px: 3,
            borderRadius: '12px', mb: 3,
          }}
          dir={direction}
        >
          {/* Text content */}
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <a
                href={process.env.REACT_APP_WWW_HOME}
                title={process.env.REACT_APP_WWW_HOME}
                style={{ textDecoration: 'none', flexShrink: 0 }}
                rel="noopener noreferrer"
              >
                <Box
                  component="img"
                  src={process.env.PUBLIC_URL + '/favicon.svg'}
                  title={process.env.REACT_APP_WWW_HOME}
                  alt="Surf Session Finder Logo"
                  sx={{ width: 36, height: 36, borderRadius: '50%', display: 'block' }}
                />
              </a>
              <Typography component="a" href={process.env.REACT_APP_WWW_HOME} title={process.env.REACT_APP_WWW_HOME} rel="noopener noreferrer" sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none' }}>
                Surf Session Finder
              </Typography>
            </Box>
            <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: '1.55rem', sm: '2rem' }, lineHeight: 1.15, letterSpacing: '-0.03em', color: '#fff', mb: 1 }}>
              {process.env.REACT_APP_HEADER_TITLE}
            </Typography>
            <Typography component="h2" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.85)', whiteSpace: 'pre-line' }}>
              {process.env.REACT_APP_HEADER_SUBTITLE}
            </Typography>
          </Box>

          {/* Surfer + wave asset */}
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/images/surfer.svg'}
            alt=""
            sx={{
              position: 'absolute',
              bottom: 0, left: 0, right: 0,
              width: '100%', height: 'auto',
              display: 'block',
              pointerEvents: 'none',
              zIndex: 0,
              transform: direction === 'rtl' ? 'scaleX(1)' : 'scaleX(-1)',
              opacity: 0.85,
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 38%)',
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 38%)',
            }}
          />

          {/* Dark overlay */}
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
            background: 'linear-gradient(to bottom, rgba(0,20,70,0.92) 0%, rgba(0,30,85,0.6) 35%, rgba(0,40,100,0.45) 60%, rgba(0,50,110,0.55) 82%, transparent 100%)',
          }} />
        </Box>

        {/* Main Form */}
        <form onSubmit={handleSubmit} dir={direction}>
          {/* Status Buttons */}
          {formData.preferenceGUID && (<Paper sx={{ p: 3, mb: 2, bgcolor: '#f9f9f9' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                {/* Check if preference is active */}
                {formData.preferenceGUID && (
                  // We need to track IS_ACTIVE status in formData
                  formData.isActive !== false ? (
                    <>
                      {/* Continue Now Button */}
                      <Button
                          variant="contained" 
                          onClick={handlePreviewSessions.bind(null, true)}
                          sx={{ fontSize: '1.2rem', backgroundColor: theme.palette.success.main }}
                        >
                        {t('status.showSessions')}
                      </Button>

                      {/* Stop Activity Button */}                      
                      <Button
                        variant="contained"
                        size="medium"
                        sx={{
                          backgroundColor: '#f94144', // Red color
                          fontSize: '1.2rem',
                          color: '#fff',
                          '&:hover': { backgroundColor: '#d90429' },
                        }}
                        onClick={() => {
                          if (formData.preferenceGUID) {
                            deletePreference(formData.preferenceGUID); // Call deletePreference with preferenceGUID
                          } else {
                            console.warn('No preferenceGUID available to delete');
                          }
                        }}
                      >
                        {t('status.stop')}
                      </Button>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem', mb: 2 }}>
                        {t('status.stopped')}
                      </Typography>
                      <Button
                        variant="contained"
                        size="medium"
                        sx={{
                          backgroundColor: '#0077b6', // Blue color
                          fontSize: '1.1rem',
                          color: '#fff',
                          '&:hover': { backgroundColor: '#005f8a' },
                        }}
                        onClick={() => {
                          // Clear all URL params and reload
                          window.location.href = window.location.pathname;
                        }}
                      >
                        {t('status.restart')}
                      </Button>
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          )}

          <Paper sx={{ p: 3, mb: 3 }}>
            {/* Body Top Explanation */}
            <Box sx={{ mb: 2 }}>
              <span style={{ fontSize: '1em', color: '#555' }}>
                {t('form.bodyTopExplanation')}
              </span>
            </Box>

            {/* Minimum Spots */}
            <Box sx={{ mb: 2 }}>
              {(() => {
                const minSpotsSummary = formData.minSpots === 0
                  ? t('summaryEx.anySpots')
                  : t('summaryEx.orMore', { spots: formData.minSpots });
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="h6"
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={e => {
                          e.preventDefault();
                          setSpotsVisible(v => !v);
                        }}
                        aria-expanded={spotsVisible}
                        aria-controls="spots-radio-group"
                        tabIndex={0}
                      >
                        {renderStepLabel(1, t('form.minSpots'))}
                      </Typography>
                      <MuiLinkButton
                        tabIndex={0}
                        onClick={e => {
                          e.preventDefault();
                          setSpotsVisible(v => !v);
                        }}
                        aria-expanded={spotsVisible}
                        aria-controls="spots-radio-group"
                      >
                        {minSpotsSummary} ▾
                      </MuiLinkButton>
                    </Box>
                    {spotsVisible && (
                      <RadioGroup
                        row
                        id="spots-radio-group"
                        value={formData.minSpots}
                        onChange={(e) => setFormData({ ...formData, minSpots: parseInt(e.target.value) })}
                        sx={{ '& .MuiFormControlLabel-root': { mb: 0 }, pl: 2  }}
                      >
                        <FormControlLabel value={0} control={<Radio />} label={t('form.spotsAny')} />
                        <FormControlLabel value={10} control={<Radio />} label={t('form.spots10')} />
                        <FormControlLabel value={5} control={<Radio />} label={t('form.spots5')} />
                        <FormControlLabel value={2} control={<Radio />} label={t('form.spots2')} />
                      </RadioGroup>
                    )}
                  </>
                );
              })()}
            </Box>

            {/* Session Selection */}
            <Box sx={{ mb: 2}}>
              {(() => {
                const levelsSummary = formData.selectedLevels.length === 0
                  ? t('summaryEx.allLevels')
                  : formData.selectedLevels.join(' ' + t('summaryEx.or') + ' ');
                return (
                  <>
                      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="h6"
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={e => {
                          e.preventDefault();
                          setLevelsVisible(v => !v);
                        }}
                        aria-expanded={levelsVisible}
                        aria-controls="levels-checkbox-group"
                        tabIndex={0}
                      >
                        {renderStepLabel(2, t('form.sessionLevels'))}
                      </Typography>
                      <MuiLinkButton
                        tabIndex={0}
                        onClick={e => {
                          e.preventDefault();
                          setLevelsVisible(v => !v);
                        }}
                        aria-expanded={levelsVisible}
                        aria-controls="levels-checkbox-group"
                      >
                        {levelsSummary} ▾
                      </MuiLinkButton>
                    </Box>
                    {sessionLevelsLoaded && sessionLevels.length === 0 && (
                      <Box sx={{ pl: 2, py: 2 }}>
                        <span style={{ color: '#f94144', fontWeight: 600, fontSize: '1.1rem' }}>
                          {t('form.sessionLevelsError')}{' '}
                          <a
                            href="#"
                            style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}
                            onClick={e => {
                              e.preventDefault();
                              window.location.reload();
                            }}
                          >
                            {t('form.sessionLevelsRetry')}
                          </a>
                        </span>
                      </Box>
                    )}
                    {levelsVisible && (() => {
                      return (
                        <FormGroup row sx={{ pl: 2 }} id="levels-checkbox-group">
                          {sessionLevels.map((level) => (
                            <FormControlLabel
                              key={level.LEVEL_NAME}
                              control={
                                <Checkbox
                                  checked={formData.selectedLevels.includes(level.LEVEL_NAME)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        selectedLevels: [...formData.selectedLevels, level.LEVEL_NAME],
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        selectedLevels: formData.selectedLevels.filter((l) => l !== level.LEVEL_NAME),
                                      });
                                    }
                                  }}
                                  sx={{ color: '#0077b6' }}
                                />
                              }
                              label={level.LEVEL_NAME}
                            />
                          ))}
                        </FormGroup>
                      );
                    })()}
                  </>
                );
              })()}
            </Box>

            {/* Side Preference */}
            <Box sx={{ mb: 2 }}>
              {(() => {
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="h6"
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={e => {
                          e.preventDefault();
                          setSideVisible(v => !v);
                        }}
                        aria-expanded={sideVisible}
                        aria-controls="side-radio-group"
                        tabIndex={0}
                      >
                        {renderStepLabel(3, t('form.side'))}
                      </Typography>
                      {/* Side summary as MuiLinkButton */}
                      <MuiLinkButton
                        tabIndex={0}
                        onClick={e => {
                          e.preventDefault();
                          setSideVisible(v => !v);
                        }}
                        aria-expanded={sideVisible}
                        aria-controls="side-radio-group"
                      >
                        {formData.sidePreference === 'any'
                          ? t('summaryEx.sideAny')
                          : formData.sidePreference === 'LEFT'
                            ? t('summaryEx.sideLeft')
                            : t('summaryEx.sideRight')} ▾
                      </MuiLinkButton>
                    </Box>
                    {sideVisible && (
                      <RadioGroup
                        row
                        id="side-radio-group"
                        value={formData.sidePreference}
                        onChange={(e) => setFormData({ ...formData, sidePreference: e.target.value as any })}
                        sx={{ '& .MuiFormControlLabel-root': { mb: 0, pl: 2 } }}
                      >
                        <FormControlLabel value="any" control={<Radio sx={{ color: '#0077b6' }} />} label={t('form.sideAny')} />
                        <FormControlLabel value="RIGHT" control={<Radio sx={{ color: '#0077b6' }} />} label={t('form.sideRight')} />
                        <FormControlLabel value="LEFT" control={<Radio sx={{ color: '#0077b6' }} />} label={t('form.sideLeft')} />
                      </RadioGroup>
                    )}
                  </>
                );
              })()}
            </Box>

            {/* Preferred Days */}
            <Box sx={{ mb: 2 }}>
              {(() => {
                // Preferred days summary text
                const dayNames = [
                  '',
                  t('days.sunday'),
                  t('days.monday'),
                  t('days.tuesday'),
                  t('days.wednesday'),
                  t('days.thursday'),
                  t('days.friday'),
                  t('days.saturday')
                ];
                const daysSummary = formData.preferredDayOfWeek.includes(0)
                  ? t('summaryEx.anyDay')
                  : t('summaryEx.days', { days: formData.preferredDayOfWeek.map(d => dayNames[d]).join(' ' + t('summaryEx.or') + ' ') });
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="h6"
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={e => {
                          e.preventDefault();
                          setDaysVisible(v => !v);
                        }}
                        aria-expanded={daysVisible}
                        aria-controls="days-checkbox-group"
                        tabIndex={0}
                      >
                        {renderStepLabel(4, t('form.days'))}
                      </Typography>
                      <MuiLinkButton
                        tabIndex={0}
                        onClick={e => {
                          e.preventDefault();
                          setDaysVisible(v => !v);
                        }}
                        aria-expanded={daysVisible}
                        aria-controls="days-checkbox-group"
                      >
                        {daysSummary} ▾
                      </MuiLinkButton>
                    </Box>
                    {daysVisible && (
                      <FormGroup row sx={{ pl: 2, flexWrap: 'wrap', alignItems: 'center' }} id="days-checkbox-group">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.preferredDayOfWeek.includes(0)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, preferredDayOfWeek: [0] });
                                } else {
                                  setFormData({ ...formData, preferredDayOfWeek: [] });
                                }
                              }}
                              sx={{ color: '#0077b6' }}
                            />
                          }
                          label={t('form.allDays')}
                        />
                        {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                          <FormControlLabel
                            key={day}
                            control={
                              <Checkbox
                                checked={formData.preferredDayOfWeek.includes(day)}
                                onChange={(e) => {
                                  let newDays = [...formData.preferredDayOfWeek];
                                  if (e.target.checked) {
                                    newDays = newDays.filter((d) => d !== 0);
                                    newDays.push(day);
                                  } else {
                                    newDays = newDays.filter((d) => d !== day);
                                  }
                                  if (newDays.length === 0) {
                                    newDays = [0];
                                  }
                                  setFormData({ ...formData, preferredDayOfWeek: newDays });
                                }}
                                sx={{ color: '#0077b6' }}
                              />
                            }
                            label={[t('days.sunday'), t('days.monday'), t('days.tuesday'), t('days.wednesday'), t('days.thursday'), t('days.friday'), t('days.saturday')][day - 1]}
                          />
                        ))}
                      </FormGroup>
                    )}
                  </>
                );
              })()}
            </Box>

            {/* Preferred Time of Day */}
            <Box sx={{ mb: 2 }}>
              {(() => {
                const timeSummary = formData.preferredTimeOfDay.length === 0
                  ? t('summaryEx.anyTime')
                  : t('summaryEx.times', { times: formData.preferredTimeOfDay.sort().join(' ' + t('summaryEx.or') + ' ') });
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="h6"
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={e => {
                          e.preventDefault();
                          setTimeVisible(v => !v);
                        }}
                        aria-expanded={timeVisible}
                        aria-controls="time-section"
                        tabIndex={0}
                      >
                        {renderStepLabel(5, t('form.time'))}
                      </Typography>
                      <MuiLinkButton
                        tabIndex={0}
                        onClick={e => {
                          e.preventDefault();
                          setTimeVisible(v => !v);
                        }}
                        aria-expanded={timeVisible}
                        aria-controls="time-section"
                      >
                        {timeSummary} ▾
                      </MuiLinkButton>
                    </Box>
                    {timeVisible && (
                      <Box id="time-section" sx={{ display: 'flex', alignItems: 'center', gap: 3, pr: 3, pl: 2, mt: 1 }}>
                        <Typography variant="body1">{getTimeDisplayText()}</Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={openTimeDialog}
                          sx={{ minWidth: 'auto', px: 3, py: 1 }}
                        >
                          {t('form.timeSelect')}
                        </Button>
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>

            {/* Summary */}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  if (formData.selectedLevels.length === 0) {
                    setMessage({ type: 'error', text: t('dialogs.preview.selectSession') });
                    setShowAlert(true);
                    setLevelsVisible(true);
                    return;
                  }
                  if (emailInputRef.current) {
                    try {
                      emailInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } catch (e) {
                      // ignore scroll failures on some browsers
                    }
                    emailInputRef.current.focus();
                  }
                }}
                sx={{ fontSize: '1.1rem', color: '#fff' }}
              >
                {t('dialogs.preview.set_alert')}
              </Button>
              <Button
                variant="contained"
                onClick={handlePreviewSessions.bind(null, false)}
                sx={{ fontSize: '1.1rem' }}
              >
                {t('dialogs.preview.button')}
              </Button>
            </Box>
          </Paper>

          {/* Automatic Activation Section */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#f9f9f9', borderRadius: '16px' }}>
            {/* Expiry Period 
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">{t('form.expiry')}</Typography>
              <RadioGroup
                value={formData.expiryPeriod}
                onChange={(e) => setFormData({ ...formData, expiryPeriod: parseInt(e.target.value) })}
                sx={{ '& .MuiFormControlLabel-root': { mb: 0 } }}
              >
                <FormControlLabel value={4} control={<Radio />} label={t('form.expiryMonth')} />
                <FormControlLabel value={25} control={<Radio />} label={t('form.expiryHalfYear')} />
                <FormControlLabel value={52} control={<Radio />} label={t('form.expiryYear')} />
              </RadioGroup>
            </Box> */}

            {/* Start Time (how long in advance to be notified) */}
            <Box sx={{ mb: 3 }}>
              {(() => {
                const noticeMap: Record<number, string> = {
                  2: t('form.notice2h'), 12: t('form.notice12h'),
                  24: t('form.notice24h'), 48: t('form.notice48h'), 168: t('form.noticeWeek'),
                };
                const startTimeSummary = noticeMap[formData.startTime] ?? t('form.notice24h');
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="h6"
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={e => {
                          e.preventDefault();
                          setStartTimeVisible(v => !v);
                        }}
                        aria-expanded={startTimeVisible}
                        aria-controls="start-time-radio-group"
                        tabIndex={0}
                      >
                        {renderStepLabel(6, t('form.noticePeriod'))}
                      </Typography>
                      <MuiLinkButton
                        tabIndex={0}
                        onClick={e => {
                          e.preventDefault();
                          setStartTimeVisible(v => !v);
                        }}
                        aria-expanded={startTimeVisible}
                        aria-controls="start-time-radio-group"
                      >
                        {startTimeSummary} ▾
                      </MuiLinkButton>
                    </Box>
                    {startTimeVisible && (
                      <RadioGroup
                        row
                        id="start-time-radio-group"
                        value={formData.startTime}
                        onChange={(e) => setFormData({ ...formData, startTime: parseInt(e.target.value) })}
                        sx={{ '& .MuiFormControlLabel-root': { mb: 0 }, pl: 2 }}
                      >
                        <FormControlLabel value={2} control={<Radio />} label={t('form.notice2h')} />
                        <FormControlLabel value={12} control={<Radio />} label={t('form.notice12h')} />
                        <FormControlLabel value={24} control={<Radio />} label={t('form.notice24h')} />
                        <FormControlLabel value={48} control={<Radio />} label={t('form.notice48h')} />
                        <FormControlLabel value={168} control={<Radio />} label={t('form.noticeWeek')} />
                      </RadioGroup>
                    )}
                  </>
                );
              })()}
            </Box>

            {/* Email Input */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                {renderStepLabel(7, t('form.email'))}
              </Typography>
              <TextField
                type="email"
                value={formData.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                inputRef={emailInputRef}
                required
                fullWidth
                size="small"
                error={!!emailError || missingEmail}
                // helperText={emailError || (missingEmail ? t('form.required') : '')}
                autoComplete="email"
                name="email"
                sx={
                  missingEmail
                    ? { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#f94144' } } }
                    : undefined
                }
              />
              <Button
                variant="contained"
                onClick={sendVerificationCode}
                sx={{ mt: 1, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: '1.1rem', color: '#fff' }}
              >
                {t('form.sendCode')}
              </Button>
            </Box>
            {showBelowCode && <>
            {/* Verification Code */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                {renderStepLabel(8, t('form.code'))}
              </Typography>
              <TextField
                inputRef={verificationCodeRef}
                value={formData.verificationCode}
                onChange={(e) => setFormData({ ...formData, verificationCode: e.target.value })}
                placeholder={t('form.codePlaceholder')}
                size="small"
                error={missingCode}
                // helperText={missingCode ? t('form.required') : ''}
                sx={
                  missingCode
                    ? { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#f94144' } } }
                    : undefined
                }
              />
            </Box>

            {/* Push Notification Checkbox & iOS UX */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.pushToken}
                      onChange={async (e) => {
                        await handlePushCheckboxChange(e.target.checked);
                      }}
                    />
                  }
                  label={t('push.checkbox')}
                  sx={{
                    mr: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '1rem',
                      color: 'text.secondary',
                      fontWeight: 'bold',
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!formData.pushToken || loading}
                  onClick={handleTestPushClick}
                >
                  {t('push.sendTest')}
                </Button>
              </Box>
              {/* iOS Safari Home Screen Instructions Link */}
              {isIOS_Safari_NotPWA && (
                <Box>
                  <a
                    href="#"
                    style={{ color: 'rgba(3, 7, 252, 1)', textDecoration: 'underline', fontWeight: 500 }}
                    onClick={e => {
                      e.preventDefault();
                      setShowIphoneHomeScreenDialog(true);
                      console.log('[Push UX] Opened iPhone Home Screen instructions modal');
                    }}
                  >
                    {t('push.iosHomeScreenLink')}
                  </a>
                </Box>
              )}
            </Box>
            {/* iPhone Home Screen Instructions Modal */}
            <Dialog
              open={showIphoneHomeScreenDialog}
              onClose={() => setShowIphoneHomeScreenDialog(false)}
              maxWidth="xs"
              fullWidth
              dir={direction}
            >
              <DialogTitle sx={{ textAlign: 'center' }}>{t('push.iosHomeScreenTitle')}</DialogTitle>
              <DialogContent>
                <Box sx={{ py: 2 }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {t('push.iosHomeScreenDesc')}
                  </Typography>
                  <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
                    <li>{t('push.iosHomeScreenStep1')}</li>
                    <li>{t('push.iosHomeScreenStep2')}</li>
                    <li>{t('push.iosHomeScreenStep3')}</li>
                  </ol>
                </Box>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center' }}>
                <Button onClick={() => setShowIphoneHomeScreenDialog(false)} variant="contained">
                  {t('dialogs.close')}
                </Button>
              </DialogActions>
            </Dialog>
            {/* FCM Error Dialog */}
            <Dialog
              open={showFcmErrorDialog}
              onClose={() => setShowFcmErrorDialog(false)}
              maxWidth="sm"
              fullWidth
              dir={direction}
            >
              <DialogTitle sx={{ textAlign: 'center' }}>
                {t('push.fcmErrorDialog.title')}
              </DialogTitle>
              <DialogContent>
                {fcmErrorDetails && (
                  <Box sx={{ mt: 2, background: '#f5f5f5', p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {fcmErrorDetails}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {t('push.fcmErrorDialog.desc')}
                    <br />
                    <strong>{t('push.fcmErrorDialog.step1')}</strong> <span style={{fontSize: '1.2em'}}>🔒</span> <strong>{t('push.fcmErrorDialog.step2')}</strong>
                    <br />
                    {t('push.fcmErrorDialog.step3')}
                    <br />
                    {t('push.fcmErrorDialog.step4')}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      window.open(
                        'https://www.google.com/search?q=enable+notifications+for+site+in+browser',
                        '_blank'
                      );
                    }}
                  >
                    {t('push.fcmErrorDialog.helpBtn')}
                  </Button>
                </Box>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center' }}>
                <Button
                  onClick={() => setShowFcmErrorDialog(false)}
                  variant="contained"
                >
                  {t('push.fcmErrorDialog.close')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* FCM Initializing Dialog */}
            <Dialog
              open={showFcmInitDialog}
              onClose={() => {}}
              maxWidth="xs"
              fullWidth
              dir={direction}
              PaperProps={{ style: { pointerEvents: 'none' } }}
            >
              <DialogTitle sx={{ textAlign: 'center' }}>
                {t('dialogs.fcmInitDialog.title')}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                  <CircularProgress size={36} />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    {t('dialogs.fcmInitDialog.trying')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('dialogs.fcmInitDialog.prompt')}
                  </Typography>
                </Box>
              </DialogContent>
            </Dialog>

            <Button
              type="submit"
              variant="contained"
              color="success"
              size="large"
              disabled={submitDisabled}
              sx={{
                fontSize: '1.3rem',
                px: 4,
                py: 1.5,
                // Custom disabled green color
                backgroundColor: (theme) =>
                  submitDisabled
                    ? '#b7e0b0' // "disabled green"
                    : theme.palette.success.main,
                color: '#fff',
                '&.Mui-disabled': {
                  backgroundColor: '#b7e0b0',
                  color: '#fff',
                },
              }}
            >
              {loading
              ? t('form.loading')
              : formData.preferenceGUID
                ? t('form.update')
                : t('form.add')}
            </Button>

            </>}

            {/* Beta Agreement */}
            <Box sx={{ mt: 1 }}>
              <span style={{ fontSize: '1em', color: '#555' }}>
                {t('form.betaAgreement')}
              </span>
            </Box>
          </Paper>
        </form>

        {/* Donation Section */}
        <Paper sx={{ p: 3, mb: 2, bgcolor: '#f9f9f9' }} dir={direction}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            {t('donate.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <form
              action="https://www.paypal.com/donate"
              method="post"
              target="_top"
              style={{ margin: 0 }}
            >
              <input type="hidden" name="business" value="CPJ95856Q2KGN" />
              <input type="hidden" name="currency_code" value={process.env.REACT_APP_DONATE_CURRENCY || 'USD'} />
              <input type="hidden" name="no_recurring" value="0" />
              <input
                type="hidden"
                name="item_name"
                value="SurfSessionFinder - made by surfers, for surfers"
              />
              <input
                type="image"
                src="https://www.paypalobjects.com/en_US/i/btn/btn_donate_LG.gif"
                name="submit"
                title="PayPal - The safer, easier way to pay online!"
                alt="Donate with PayPal button"
                style={{ border: 0 }}
              />
            </form>
          </Box>
        </Paper>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            py: 1,
            mt: 2,
            borderTop: '1px solid #e0e0e0',
            px: { xs: 2, sm: 4 },
            background: 'transparent',
            justifyContent: 'center',
          }}
          dir={'ltr'}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 2 }}>
            <a
              href={'https://www.SurfSessionFinder.com'}
              title={'https://www.SurfSessionFinder.com'}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', height: '100%' }}
            >
              <Box
                component="img"
                src={process.env.PUBLIC_URL + '/images/logo-192x192.png'}
                title={process.env.REACT_APP_WWW_HOME}
                alt="Surf Session Finder Logo"
                sx={{
                  height: '100%',
                  width: 'auto',
                  maxHeight: '64px',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </a>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                <a
                  href="mailto:feedback@surfsessionfinder.com"
                  style={{ color: '#1976d2', textDecoration: 'underline' }}
                >
                  feedback@surfsessionfinder.com
                </a>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <a
                  href={process.env.REACT_APP_WWW_HOME}
                  title={process.env.REACT_APP_WWW_HOME}
                  style={{ color: '#1976d2', textDecoration: 'underline' }}
                >
                  {process.env.REACT_APP_WWW_HOME_TEXT}
                </a>
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Alert Modal Dialog */}
        <Dialog
          open={showAlert}
          onClose={handleAlertClose}
          maxWidth="sm"
          fullWidth
          dir={direction}
        >
          <DialogTitle sx={{ textAlign: 'center' }}>
            {message?.type === 'success' ? t('dialogs.success.title') : t('dialogs.error.title')}
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              sx={{ textAlign: 'center', py: 2 }}
              component="div"
              dangerouslySetInnerHTML={
                message?.text
                  ? { __html: sanitizeHtml(message.text, { allowedTags: ['b', 'br'], allowedAttributes: { b: [] } }) }
                  : undefined
              }
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button
              onClick={handleAlertClose}
              variant="contained"
              color={message?.type === 'success' ? 'success' : 'error'}
            >
              {t('dialogs.close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Time Selection Modal Dialog */}
        <Dialog
          open={showTimeDialog}
          onClose={handleTimeCancel}
          maxWidth="md"
          fullWidth
          dir={direction}
        >
          <DialogTitle sx={{ textAlign: 'center' }}>
            {t('dialogs.time.title')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {generateTimeOptions().map((timeOption) => (
                <FormControlLabel
                  key={timeOption}
                  control={
                    <Checkbox
                      checked={tempSelectedTimes.includes(timeOption)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTempSelectedTimes([...tempSelectedTimes, timeOption]);
                        } else {
                          setTempSelectedTimes(tempSelectedTimes.filter(t => t !== timeOption));
                        }
                      }}
                    />
                  }
                  label={timeOption}
                  sx={{
                    minWidth: '80px',
                    m: 0,
                    padding: '2px 4px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                    '& .MuiFormControlLabel-label': {
                      fontSize: '1rem', // Larger font size for time options
                      fontWeight: 500,
                    },
                    '& .MuiCheckbox-root': {
                      padding: '6px',
                      transform: 'scale(1.2)', // Make checkboxes bigger in time dialog
                    },
                  }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setTempSelectedTimes([]);
              }}
              style={{
                textDecoration: 'underline',
                color: '#1976d2',
                cursor: 'pointer',
                position: 'absolute',
                right: '24px',
              }}
            >
              {t('dialogs.time.clear')}
            </a>
            <Button
              onClick={handleTimeCancel}
              variant="outlined"
            >
              {t('dialogs.time.cancel')}
            </Button>
            <Button
              onClick={handleTimeConfirm}
              variant="contained"
            >
              {t('dialogs.time.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Preview Sessions Modal Dialog */}
        <Dialog
          open={showPreviewDialog}
          onClose={() => setShowPreviewDialog(false)}
          maxWidth="md"
          fullWidth
          dir={direction}
        >
          <DialogContent>
            {previewSessions.length === 0 ? (
              <Typography
                variant="body1"
                sx={{ textAlign: 'center', py: 3 }}
                component="div"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(t('dialogs.preview.none'), { allowedTags: ['b', 'br'], allowedAttributes: { b: [] } })
                }}
              />
            ) : (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                  {t('dialogs.preview.found', { count: previewSessions.length })}
                </Typography>
                <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {previewSessions.map((session, index) => (
                    <Box 
                      key={session.ID}
                      sx={{ 
                        p: 1, 
                        mb: 0.5, 
                        border: '1px solid #ddd', 
                        borderRadius: 1,
                        backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                        cursor: 'pointer', // Add pointer cursor
                        transition: 'background 0.2s',
                        '&:hover': {
                          backgroundColor: '#e0f7fa',
                        },
                      }}
                      onClick={() => {
                        // Returning users (existing preferenceGUID) redirect to the pool schedule;
                        // new users just close the preview dialog.
                        if (formData.preferenceGUID) {
                          const url = session.EXT_URL; // process.env.REACT_APP_POOL_SCHEDULE_URL;
                          if (url) {
                            window.open(url, '_blank');
                          } else {
                            console.error('POOL_SCHEDULE_URL is not defined');
                          }
                        } else {
                          setShowPreviewDialog(false);
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.2 }}>
                            {session.SESSION_TITLE} - {session.SESSION_SIDE}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1 }} color="text.secondary">
                            {
                              // Parse the session date and calculate days from today
                              (() => {
                                const today = new Date();
                                // sessionDate is in Local
                                const sessionDate = new Date(session.SESSION_DATETIME_UTCISO); // No need for Date.parse()
                                // Normalize both dates to midnight local time to avoid timezone issues
                                const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                const sessionMidnight = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
                                const diffDays = Math.floor((sessionMidnight.getTime() - todayMidnight.getTime()) / (1000 * 60 * 60 * 24));
                               
                                // Hebrew day names mapping (0 = Sunday)
                                const hebrewDayNames = [
                                  t('days.sunday'),
                                  t('days.monday'),
                                  t('days.tuesday'),
                                  t('days.wednesday'),
                                  t('days.thursday'),
                                  t('days.friday'),
                                  t('days.saturday'),
                                ];
                                const dayOfWeek = hebrewDayNames[sessionDate.getDay()];
                                
                                if (diffDays === 0) return `${t('sessions.today')}, ${dayOfWeek}`;
                                if (diffDays === 1) return `${t('sessions.tomorrow')}, ${dayOfWeek}`;
                                if (diffDays === 2) return `${t('sessions.dayAfterTomorrow')}, ${dayOfWeek}`;
                                if (diffDays > 2) return `${t('sessions.inXDays', { diffDays })}, ${dayOfWeek}`;
                                console.warn('Unexpected diffDays:', diffDays, 'for datetime:', session.SESSION_DATETIME_UTCISO);
                                return '';
                              })()
                            }
                            {' '}
                            {session.SESSION_DATE}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1 }} color="text.secondary">
                            {t('sessions.at')} {session.SESSION_TIME}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              color: session.AVAILABLE_SPOTS >= 10 ? 'green' : 
                                     session.AVAILABLE_SPOTS >= 5 ? 'orange' : 'red'
                            }}
                          >
                            {session.AVAILABLE_SPOTS} {t('sessions.spots')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button
              onClick={() => setShowPreviewDialog(false)}
              variant="contained"
            >
              {t('dialogs.preview.set_alert')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Action Result Dialog */}
        <Dialog
          open={showActionDialog}
          onClose={() => setShowActionDialog(false)}
          maxWidth="sm"
          fullWidth
          dir={direction}
        >
          <DialogTitle sx={{ textAlign: 'center' }}>
            {actionDialogMessage?.type === 'success' ? t('dialogs.success.title') : t('dialogs.error.title')}
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              sx={{ textAlign: 'center', py: 2 }}
              component="div"
              dangerouslySetInnerHTML={
                actionDialogMessage?.text
                  ? { __html: sanitizeHtml(actionDialogMessage.text, { allowedTags: ['b', 'br'], allowedAttributes: { b: [] } }) }
                  : undefined
              }
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button
              onClick={() => setShowActionDialog(false)}
              variant="contained"
              color={actionDialogMessage?.type === 'success' ? 'success' : 'error'}
            >
              {t('dialogs.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default SessionFinderForm;
